import {
  Box, Card, CardContent, Typography, Button, TextField, Table, TableBody,
  TableCell, TableHead, TableRow, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, MenuItem, Chip,
} from '@mui/material';
import { Add, Edit, Delete, ExpandMore, ExpandLess } from '@mui/icons-material';
import { useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import api from '../../api/axios';
import { showSnackbar } from '../ui/uiSlice';

function CategoryRow({ cat, depth, allCats, onEdit, onDelete }) {
  const [expanded, setExpanded] = useState(true);
  const hasChildren = cat.children?.length > 0;

  return (
    <>
      <TableRow hover>
        <TableCell sx={{ pl: 2 + depth * 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {hasChildren && (
              <IconButton size="small" onClick={() => setExpanded(p => !p)}>
                {expanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
              </IconButton>
            )}
            {!hasChildren && <Box sx={{ width: 32 }} />}
            <Typography variant="body2" fontWeight={depth === 0 ? 600 : 400}>{cat.name}</Typography>
            {cat.name_ur && <Typography variant="caption" color="text.secondary">({cat.name_ur})</Typography>}
          </Box>
        </TableCell>
        <TableCell>{cat.parent_name || '—'}</TableCell>
        <TableCell><Chip label={cat.is_active ? 'Active' : 'Inactive'} size="small" color={cat.is_active ? 'success' : 'default'} /></TableCell>
        <TableCell>
          <IconButton size="small" onClick={() => onEdit(cat)}><Edit fontSize="small" /></IconButton>
          <IconButton size="small" color="error" onClick={() => onDelete(cat.id)}><Delete fontSize="small" /></IconButton>
        </TableCell>
      </TableRow>
      {expanded && cat.children?.map(child => (
        <CategoryRow key={child.id} cat={child} depth={depth + 1} allCats={allCats} onEdit={onEdit} onDelete={onDelete} />
      ))}
    </>
  );
}

export default function CategoriesPage() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [categories, setCategories] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const { register, handleSubmit, reset, formState: { errors } } = useForm();

  const load = useCallback(async () => {
    const { data } = await api.get('/categories');
    setCategories(data.data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const flatList = (cats, result = []) => {
    cats.forEach(c => { result.push(c); if (c.children?.length) flatList(c.children, result); });
    return result;
  };
  const flat = flatList(categories);

  const openDialog = (cat = null) => {
    setEditing(cat);
    reset(cat ? { name: cat.name, name_ur: cat.name_ur, parent_id: cat.parent_id || '', description: cat.description } : {});
    setDialogOpen(true);
  };

  const onSubmit = async (data) => {
    try {
      if (editing) await api.put(`/categories/${editing.id}`, data);
      else await api.post('/categories', data);
      dispatch(showSnackbar({ message: editing ? 'Category updated' : 'Category created', severity: 'success' }));
      setDialogOpen(false); load();
    } catch (e) {
      dispatch(showSnackbar({ message: e.response?.data?.message || 'Error', severity: 'error' }));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this category?')) return;
    await api.delete(`/categories/${id}`);
    dispatch(showSnackbar({ message: 'Category deleted', severity: 'success' }));
    load();
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight={700}>{t('nav.categories')}</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => openDialog()}>{t('common.add')} Category</Button>
      </Box>

      <Card>
        <CardContent>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('common.name')}</TableCell>
                <TableCell>Parent</TableCell>
                <TableCell>{t('common.status')}</TableCell>
                <TableCell>{t('common.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {categories.map(cat => (
                <CategoryRow key={cat.id} cat={cat} depth={0} allCats={flat} onEdit={openDialog} onDelete={handleDelete} />
              ))}
              {categories.length === 0 && (
                <TableRow><TableCell colSpan={4} align="center"><Typography color="text.secondary" py={3}>No categories yet</Typography></TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit Category' : 'Add Category'}</DialogTitle>
        <DialogContent>
          <Box component="form" id="cat-form" onSubmit={handleSubmit(onSubmit)} sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField fullWidth label="Name (English)" {...register('name', { required: true })} error={!!errors.name} />
            <TextField fullWidth label="نام (Urdu)" {...register('name_ur')} inputProps={{ dir: 'rtl' }} />
            <TextField fullWidth select label="Parent Category" defaultValue="" {...register('parent_id')}>
              <MenuItem value="">— No Parent —</MenuItem>
              {flat.filter(c => !editing || c.id !== editing.id).map(c => (
                <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
              ))}
            </TextField>
            <TextField fullWidth label={t('common.notes')} {...register('description')} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button variant="contained" type="submit" form="cat-form">{t('common.save')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
